/**
 * The app-side emit host for the Evidence post-upgrade flow — the welcome popup
 * and the three-step onboarding modal (`@heidi/commerce`).
 *
 * Ruling 24: the domain side decides WHEN each event fires, which step it is on
 * and every `intl`-formatted label; these two functions add the `event_name`, the
 * destination routing, and nothing else.
 *
 * NEITHER SURFACE HAS A VIEW SEAM, because neither has ever emitted one — the
 * popup's impression is the modal queue's `mark modal as viewed` write and the
 * modal reports its step on every click instead. No view emit is invented here
 * to round the pair out; that would be a new event, not a move.
 *
 * ROUTING IS PRESERVED, NOT TIDIED: both surfaces' clicks pass no `options` and
 * so reach every destination, which is what they have always done. Neither
 * `button_clicked` nor anything else here is on the Snowplow denylist, so no
 * `options` object is needed for suppression and none should be added.
 */
import { track, TrackingEventName } from '@heidi/analytics/events';
import { ButtonAction } from '@heidi/analytics/vocabulary';
import {
  EVIDENCE_POST_UPGRADE_ONBOARDING_ACTION,
  EVIDENCE_POST_UPGRADE_WELCOME_ACTION,
  type EvidencePostUpgradeOnboardingActionToken,
  type EvidencePostUpgradeWelcomeActionToken,
  type TrackEvidencePostUpgradeOnboardingClick,
  type TrackEvidencePostUpgradeWelcomeClick,
} from '@heidi/commerce/contracts';
import { TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN } from './trialsAndUpgradesScreenNames';

/**
 * The package's `action` tokens bound to the app enum members that declare them.
 *
 * Two maps rather than one, because the two surfaces are two screens with two
 * action vocabularies; a merged map would let a welcome token be emitted against
 * the onboarding screen and still type-check.
 * `trackEvidencePostUpgradeAnalytics.test.ts` asserts every entry maps a token to
 * an enum member of the identical value.
 */
const ONBOARDING_ACTION_BY_TOKEN = {
  [EVIDENCE_POST_UPGRADE_ONBOARDING_ACTION.next]:
    ButtonAction.EvidencePostUpgradeOnboardingNext,
  [EVIDENCE_POST_UPGRADE_ONBOARDING_ACTION.gotIt]:
    ButtonAction.EvidencePostUpgradeOnboardingGotIt,
  [EVIDENCE_POST_UPGRADE_ONBOARDING_ACTION.tryNow]:
    ButtonAction.EvidencePostUpgradeOnboardingTryNow,
  [EVIDENCE_POST_UPGRADE_ONBOARDING_ACTION.viewGuide]:
    ButtonAction.EvidencePostUpgradeOnboardingViewGuide,
  [EVIDENCE_POST_UPGRADE_ONBOARDING_ACTION.learnMore]:
    ButtonAction.EvidencePostUpgradeOnboardingLearnMore,
} as const satisfies Record<EvidencePostUpgradeOnboardingActionToken, ButtonAction>;

const WELCOME_ACTION_BY_TOKEN = {
  [EVIDENCE_POST_UPGRADE_WELCOME_ACTION.seeWhatsNew]:
    ButtonAction.EvidencePostUpgradeWelcomeSeeWhatsNew,
  [EVIDENCE_POST_UPGRADE_WELCOME_ACTION.close]:
    ButtonAction.EvidencePostUpgradeWelcomeClose,
} as const satisfies Record<EvidencePostUpgradeWelcomeActionToken, ButtonAction>;

/** One of the onboarding modal's five CTAs was clicked. */
export const trackEvidencePostUpgradeOnboardingClick: TrackEvidencePostUpgradeOnboardingClick =
  ({ screen_name, action, ...rest }) => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name],
        action: ONBOARDING_ACTION_BY_TOKEN[action],
        ...rest,
      },
    });
  };

/** The welcome popup's dismiss or "See what's new" was clicked. */
export const trackEvidencePostUpgradeWelcomeClick: TrackEvidencePostUpgradeWelcomeClick =
  ({ screen_name, action, ...rest }) => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name],
        action: WELCOME_ACTION_BY_TOKEN[action],
        ...rest,
      },
    });
  };
